import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Upload, Play, Pause, Download, X, Wand2, Loader2, RefreshCw, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import useGreenScreen from "../lib/useGreenScreen";

/**
 * StudioVodBgRemove — client-side background remover for pre-recorded
 * videos (uploaded MP4 / WebM / MOV files). This complements the live
 * Green Screen tools by letting creators strip the background from a
 * clip they've already shot — perfect for repurposing raw footage as
 * a transparent/branded asset for later broadcasts.
 *
 * Why client-side?
 *   Running MediaPipe Selfie Segmentation frame-by-frame server-side
 *   would require FFmpeg + heavy GPU workers we don't currently have.
 *   Browsers already run Selfie Segmentation at 30 fps on commodity
 *   hardware, so we lean on the client for the heavy lifting and
 *   record the resulting canvas via MediaRecorder.
 *
 * Flow:
 *   1. User uploads a video (<200 MB, <5 min recommended).
 *   2. We pipe `video.captureStream()` through the shared
 *      `useGreenScreen` hook — same compositor the live studio uses,
 *      so the visual quality is identical (Zoom-style feathered edges).
 *   3. The host previews the composite live while the video plays.
 *   4. Hitting "Record" starts a MediaRecorder against the
 *      compositor's output track and captures a WebM to disk.
 */
export default function StudioVodBgRemove() {
  const fileInputRef = useRef(null);
  const videoElRef = useRef(null);
  const previewElRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [srcUrl, setSrcUrl] = useState("");
  const [srcName, setSrcName] = useState("");
  const [videoStream, setVideoStream] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [gsOn, setGsOn] = useState(true);
  const [bgMode, setBgMode] = useState("none"); // none | blur | image
  const [bgUrl, setBgUrl] = useState("");
  const [bgDataUrl, setBgDataUrl] = useState("");
  const [blurLevel, setBlurLevel] = useState("medium");
  const [library, setLibrary] = useState([]);
  const [recording, setRecording] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [downloadName, setDownloadName] = useState("");
  const [smartFrame, setSmartFrame] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareTitle, setShareTitle] = useState("");
  const [crossPostSocial, setCrossPostSocial] = useState(true);
  const nav = useNavigate();

  // Lazy-load the same background library the live GS uses so users
  // aren't hunting for backgrounds twice across the studio.
  useEffect(() => {
    let cancelled = false;
    api.get("/greenscreen/backgrounds")
      .then(({ data }) => { if (!cancelled) setLibrary(data?.items || []); })
      .catch(() => { if (!cancelled) setLibrary([]); });
    return () => { cancelled = true; };
  }, []);

  // Pipe the uploaded video element through the same compositor the
  // live studio uses. `videoStream` is captured off <video> once it
  // reports metadata + is playing (some browsers need a real frame
  // before captureStream() produces active tracks).
  const background = useMemo(
    () => ({ mode: bgMode, url: bgUrl, dataUrl: bgDataUrl, blurLevel }),
    [bgMode, bgUrl, bgDataUrl, blurLevel]
  );
  const chroma = useMemo(
    () => ({ enabled: true, mode: "person", color: "#00FF00", similarity: 0.55, smoothness: 0.15 }),
    []
  );
  const { track: composedTrack } = useGreenScreen({
    stream: videoStream,
    enabled: gsOn,
    background,
    chroma,
    smartFrame,
    width: 1280,
    height: 720,
  });

  // Route the composited track into the visible preview <video>.
  useEffect(() => {
    const el = previewElRef.current;
    if (!el) return;
    if (composedTrack) {
      try { el.srcObject = new MediaStream([composedTrack]); } catch { /* ignore */ }
    } else if (videoStream) {
      el.srcObject = videoStream;
    } else {
      el.srcObject = null;
    }
  }, [composedTrack, videoStream]);

  const onFile = useCallback((e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) {
      toast.error("Video must be under 200 MB.");
      return;
    }
    if (!/^video\//.test(f.type)) {
      toast.error("Please pick a video file (MP4, WebM, MOV).");
      return;
    }
    // Revoke old blob if any so we don't leak memory across uploads.
    if (srcUrl) { try { URL.revokeObjectURL(srcUrl); } catch { /* noop */ } }
    const url = URL.createObjectURL(f);
    setSrcUrl(url);
    setSrcName(f.name);
    setDownloadUrl("");
    setDownloadName("");
    setPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    setVideoStream(null);
  }, [srcUrl]);

  // Once the video element has metadata, capture its stream so
  // useGreenScreen can composite off it. Wait for `loadeddata` so
  // Firefox actually produces a track (Chrome is fine on `loadedmetadata`).
  const onVideoLoaded = useCallback(() => {
    const v = videoElRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
    try {
      const s = v.captureStream ? v.captureStream() : v.mozCaptureStream?.();
      if (s) setVideoStream(s);
    } catch (err) {
      console.warn("captureStream failed:", err);
      toast.error("Your browser can't capture this video. Try Chrome or Firefox.");
    }
  }, []);

  const onTimeUpdate = useCallback(() => {
    const v = videoElRef.current;
    if (v) setCurrentTime(v.currentTime || 0);
  }, []);

  const togglePlay = useCallback(async () => {
    const v = videoElRef.current;
    if (!v) return;
    try {
      if (v.paused) { await v.play(); setPlaying(true); }
      else { v.pause(); setPlaying(false); }
    } catch {
      /* autoplay policy — user gesture required */
    }
  }, []);

  const onSeek = useCallback((e) => {
    const v = videoElRef.current;
    if (!v || !duration) return;
    const t = parseFloat(e.target.value);
    v.currentTime = t;
    setCurrentTime(t);
  }, [duration]);

  // ---- Recording ---------------------------------------------------
  // MediaRecorder captures the composited output track directly so
  // the exported clip is EXACTLY what viewers see in the preview.
  const beginRecord = useCallback(async () => {
    if (!composedTrack) {
      toast.error("Green screen is warming up — try again in a second.");
      return;
    }
    if (!videoElRef.current) return;
    // Force the source video back to 0 so the export always starts
    // clean (nobody wants a 20-second clip that skips the first half).
    try { videoElRef.current.currentTime = 0; } catch { /* ignore */ }
    const audioTrack = videoStream?.getAudioTracks?.()[0];
    const outStream = new MediaStream(audioTrack ? [composedTrack, audioTrack] : [composedTrack]);
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    let rec;
    try { rec = new MediaRecorder(outStream, { mimeType: mime, videoBitsPerSecond: 4_000_000 }); }
    catch (err) {
      toast.error("Your browser doesn't support recording — try Chrome.");
      console.warn(err);
      return;
    }
    chunksRef.current = [];
    rec.ondataavailable = (evt) => { if (evt.data && evt.data.size) chunksRef.current.push(evt.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      const name = (srcName || "clip").replace(/\.[a-z0-9]+$/i, "") + "-bg-removed.webm";
      setDownloadUrl(url);
      setDownloadName(name);
      setRecording(false);
      toast.success("Export ready — tap Download to save.");
    };
    // When the source video ends, stop the recorder automatically so
    // the user doesn't get a trailing black frame appended.
    const onEnded = () => { try { rec.state !== "inactive" && rec.stop(); } catch { /* noop */ } };
    videoElRef.current.addEventListener("ended", onEnded, { once: true });
    recorderRef.current = rec;
    try {
      rec.start(500);
      setRecording(true);
      await videoElRef.current.play();
      setPlaying(true);
      toast("Recording — playback will start from the beginning.", { duration: 3000 });
    } catch (err) {
      toast.error("Couldn't start recording.");
      console.warn(err);
      setRecording(false);
    }
  }, [composedTrack, srcName, videoStream]);

  const stopRecord = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") { try { rec.stop(); } catch { /* ignore */ } }
    const v = videoElRef.current;
    if (v && !v.paused) { try { v.pause(); } catch { /* ignore */ } setPlaying(false); }
  }, []);

  // Cleanup blob URLs on unmount so the browser doesn't cache the
  // whole video after the user leaves the page.
  useEffect(() => () => {
    if (srcUrl) { try { URL.revokeObjectURL(srcUrl); } catch { /* ignore */ } }
    if (downloadUrl) { try { URL.revokeObjectURL(downloadUrl); } catch { /* ignore */ } }
  }, [srcUrl, downloadUrl]);

  // ---- Backgrounds -------------------------------------------------
  const setBgNone = () => { setBgMode("none"); setBgUrl(""); setBgDataUrl(""); };
  const setBgBlur = (level) => {
    setBgMode("blur");
    setBgUrl("");
    setBgDataUrl("");
    if (level) setBlurLevel(level);
  };
  const applyLib = (it) => {
    setBgMode("image");
    setBgUrl(it.url || "");
    setBgDataUrl(it.data_url || "");
  };
  const onBgUpload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Background must be under 8 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setBgMode("image");
      setBgUrl("");
      setBgDataUrl(String(reader.result || ""));
    };
    reader.readAsDataURL(f);
  };
  const onGenerate = async () => {
    const p = aiPrompt.trim();
    if (!p) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/greenscreen/backgrounds/generate", { prompt: p });
      if (data?.item) {
        setLibrary((cur) => [data.item, ...cur]);
        applyLib(data.item);
        setAiPrompt("");
        toast.success("Background generated.");
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't generate background.");
    } finally { setGenerating(false); }
  };

  const fmtTime = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec < 10 ? "0" : ""}${sec}`;
  };

  // ---- Share to Reel -----------------------------------------------
  // Push the exported WebM straight into the user's Reels feed so they
  // don't have to download → re-upload elsewhere. Two-step server call:
  // (1) upload the blob to object storage via /api/uploads/video, then
  // (2) create a Video row via /api/videos with is_short + 9:16 flags
  // so it appears in the vertical Reels tab.
  const shareToReel = useCallback(async () => {
    if (!downloadUrl) return;
    const title = shareTitle.trim() || (srcName || "Reel").replace(/\.[a-z0-9]+$/i, "");
    if (title.length < 3) {
      toast.error("Give your reel a title (min 3 chars).");
      return;
    }
    setSharing(true);
    try {
      // 1) Fetch the blob back from the object URL — MediaRecorder
      //    already saved it locally, we just re-read it.
      const blob = await fetch(downloadUrl).then((r) => r.blob());
      const uploadName = (downloadName || "reel.webm").replace(/\s+/g, "_");
      const form = new FormData();
      form.append("file", blob, uploadName);
      const { data: up } = await api.post("/uploads/video", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // 2) Create the Video record — Reels feed queries the same
      //    /videos collection and filters `is_short: true`.
      const { data: vid } = await api.post("/videos", {
        title,
        description: "Background removed via Tap In Studio.",
        category: "shorts",
        is_short: true,
        format: "story",
        aspect_ratio: "9:16",
        storage_path: up?.storage_path,
        tags: ["reel", "bg-removed"],
      });
      // 3) OPTIONAL — cross-post to /social so the same clip also
      //    appears in the creator's Tap IN Social feed. Fire-and-forget
      //    so a failure here doesn't hide the "Shared" toast.
      if (crossPostSocial) {
        const apiBase = api.defaults.baseURL || "";
        const clipUrl = up?.storage_path
          ? `${apiBase.replace(/\/+$/, "")}/files/${up.storage_path}`
          : "";
        if (clipUrl) {
          api.post("/community/posts", {
            title: title.slice(0, 140),
            content: "Fresh clip — background stripped in-studio.",
            image_url: "",
            video_url: clipUrl,
            tags: ["reel", "bg-removed", "tap-in-studio"],
          }).catch(() => { /* non-fatal — reel is already published */ });
        }
      }
      toast.success(crossPostSocial ? "Shared to Reels + Social." : "Shared to your Reels feed.");
      if (vid?.id) nav(`/watch/${vid.id}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't share to Reels.");
    } finally { setSharing(false); }
  }, [downloadUrl, downloadName, shareTitle, srcName, crossPostSocial, nav]);

  return (
    <div data-testid="vod-bg-remove-page" className="min-h-screen bg-[#050810] text-white pt-20 md:pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <header className="mb-6">
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-tv-accent" /> Remove Background from Video
          </h1>
          <p className="text-white/60 text-sm mt-1 max-w-2xl">
            Upload a pre-recorded clip, pick a new background (or go transparent), then export a
            clean WebM. Runs entirely in your browser — nothing uploads to our servers.
          </p>
        </header>

        {/* Step 1 — Upload */}
        {!srcUrl && (
          <div
            data-testid="vod-bg-upload-zone"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === "Enter") fileInputRef.current?.click(); }}
            role="button"
            tabIndex={0}
            className="border-2 border-dashed border-white/20 rounded-2xl p-10 text-center hover:border-tv-accent hover:bg-white/[0.02] transition cursor-pointer"
          >
            <Upload className="w-12 h-12 text-tv-accent mx-auto mb-3" />
            <div className="text-lg font-bold">Tap to upload a video</div>
            <div className="text-white/50 text-sm mt-1">MP4, WebM, or MOV · up to 200 MB · under 5 min recommended</div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={onFile}
              className="hidden"
              data-testid="vod-bg-file-input"
            />
          </div>
        )}

        {/* Step 2 — Preview + Controls */}
        {srcUrl && (
          <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-4">
            <section className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
              {/* Hidden source <video> — this is what the compositor
                  reads FROM. We keep it in the DOM so browsers actually
                  produce frames from it. */}
              <video
                ref={videoElRef}
                src={srcUrl}
                onLoadedData={onVideoLoaded}
                onTimeUpdate={onTimeUpdate}
                onEnded={() => setPlaying(false)}
                playsInline
                crossOrigin="anonymous"
                className="hidden"
                data-testid="vod-bg-src-video"
              />
              {/* Visible preview — this is what the user sees. The
                  compositor's output track is piped in via srcObject. */}
              <div className="relative aspect-video bg-black">
                <video
                  ref={previewElRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-contain"
                  data-testid="vod-bg-preview-video"
                />
                {!videoStream && (
                  <div className="absolute inset-0 flex items-center justify-center text-white/50 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading video…
                  </div>
                )}
                {recording && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-red-500/90 text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> REC
                  </div>
                )}
              </div>

              {/* Transport */}
              <div className="p-3 flex items-center gap-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={togglePlay}
                  disabled={recording}
                  data-testid="vod-bg-play"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center"
                  title={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <div className="text-xs text-white/60 tabular-nums w-16 shrink-0">
                  {fmtTime(currentTime)} / {fmtTime(duration)}
                </div>
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  value={currentTime}
                  onChange={onSeek}
                  disabled={recording}
                  data-testid="vod-bg-seek"
                  className="flex-1 accent-tv-accent"
                />
                <button
                  type="button"
                  onClick={() => { setSrcUrl(""); setSrcName(""); setVideoStream(null); setDownloadUrl(""); }}
                  data-testid="vod-bg-clear"
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
                  title="Load a different video"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Export bar */}
              <div className="p-3 flex flex-wrap items-center gap-2 border-t border-white/10 bg-black/40">
                {!recording && !downloadUrl && (
                  <button
                    type="button"
                    onClick={beginRecord}
                    disabled={!composedTrack}
                    data-testid="vod-bg-export"
                    className="inline-flex items-center gap-1.5 bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-full disabled:opacity-40"
                  >
                    <Download className="w-3.5 h-3.5" /> Export clean video
                  </button>
                )}
                {recording && (
                  <button
                    type="button"
                    onClick={stopRecord}
                    data-testid="vod-bg-stop"
                    className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-400 text-white font-black text-xs uppercase tracking-wider px-4 py-2 rounded-full"
                  >
                    Stop &amp; save
                  </button>
                )}
                {downloadUrl && (
                  <a
                    href={downloadUrl}
                    download={downloadName || "bg-removed.webm"}
                    data-testid="vod-bg-download"
                    className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-full"
                  >
                    <Download className="w-3.5 h-3.5" /> Download {downloadName}
                  </a>
                )}
                {downloadUrl && (
                  <>
                    <input
                      type="text"
                      value={shareTitle}
                      onChange={(e) => setShareTitle(e.target.value)}
                      placeholder="Reel title (optional)"
                      maxLength={80}
                      data-testid="vod-bg-share-title"
                      className="flex-1 min-w-[10rem] bg-white/5 border border-white/10 rounded-full px-3 py-2 text-xs outline-none focus:border-tv-accent placeholder:text-white/35"
                    />
                    <label className="inline-flex items-center gap-1.5 text-xs text-white/70 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={crossPostSocial}
                        onChange={(e) => setCrossPostSocial(e.target.checked)}
                        data-testid="vod-bg-cross-post"
                        className="accent-tv-accent"
                      />
                      Cross-post to Social
                    </label>
                    <button
                      type="button"
                      onClick={shareToReel}
                      disabled={sharing}
                      data-testid="vod-bg-share-reel"
                      className="inline-flex items-center gap-1.5 bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-full disabled:opacity-50"
                      title="Publish this clip straight to your Reels feed"
                    >
                      {sharing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                      {sharing ? "Sharing…" : "Share to Reel"}
                    </button>
                  </>
                )}
                <div className="text-[10px] text-white/40 italic ml-auto">
                  {srcName || ""}
                </div>
              </div>
            </section>

            {/* Right column — Background picker */}
            <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-sm">Background</div>
                <button
                  type="button"
                  onClick={() => setGsOn((v) => !v)}
                  aria-pressed={gsOn}
                  data-testid="vod-bg-gs-toggle"
                  className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                    gsOn ? "bg-tv-accent text-black border-tv-accent" : "bg-white/5 text-white/60 border-white/15"
                  }`}
                >
                  GS {gsOn ? "ON" : "OFF"}
                </button>
              </div>

              {/* Preset row */}
              <button
                type="button"
                onClick={setBgNone}
                aria-pressed={bgMode === "none"}
                data-testid="vod-bg-none"
                className={`w-full px-2 py-2 rounded border text-[10px] uppercase tracking-wider font-black ${
                  bgMode === "none"
                    ? "bg-tv-accent text-black border-tv-accent"
                    : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                }`}
              >
                Transparent
              </button>

              {/* Blur intensity chips — light / medium / heavy so
                  creators can dial in the exact vibe (Zoom-subtle vs
                  cinematic bokeh) instead of a single one-size blur. */}
              <div>
                <div className="text-[9px] uppercase tracking-wider text-white/40 font-black mb-1">Blur</div>
                <div className="grid grid-cols-3 gap-1.5 text-[10px] uppercase tracking-wider font-black">
                  {[
                    { k: "light",  label: "Light"  },
                    { k: "medium", label: "Medium" },
                    { k: "heavy",  label: "Heavy"  },
                  ].map((b) => (
                    <button
                      key={b.k}
                      type="button"
                      onClick={() => setBgBlur(b.k)}
                      aria-pressed={bgMode === "blur" && blurLevel === b.k}
                      data-testid={`vod-bg-blur-${b.k}`}
                      className={`px-2 py-2 rounded border ${
                        bgMode === "blur" && blurLevel === b.k
                          ? "bg-tv-accent text-black border-tv-accent"
                          : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                      }`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Smart Frame toggle */}
              <button
                type="button"
                onClick={() => setSmartFrame((v) => !v)}
                aria-pressed={smartFrame}
                data-testid="vod-bg-smart-frame"
                className={`w-full text-[10px] font-black uppercase tracking-wider px-2.5 py-2 rounded-full border ${
                  smartFrame
                    ? "bg-tv-accent text-black border-tv-accent"
                    : "bg-white/5 text-white/60 border-white/15"
                }`}
                title="Auto-center the subject in the frame"
              >
                Smart Frame {smartFrame ? "ON" : "OFF"}
              </button>

              {/* Library */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/40 font-black mb-1">Library</div>
                <div className="grid grid-cols-3 gap-1.5 max-h-48 overflow-y-auto pr-1">
                  {library.slice(0, 18).map((it) => {
                    const active = (bgUrl && it.url === bgUrl) || (bgDataUrl && it.data_url === bgDataUrl);
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => applyLib(it)}
                        data-testid={`vod-bg-lib-${it.id}`}
                        title={it.title}
                        className={`aspect-video rounded overflow-hidden border ${
                          active ? "border-tv-accent ring-1 ring-tv-accent" : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        <img
                          src={it.url || it.data_url}
                          alt={it.title || ""}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    );
                  })}
                  {library.length === 0 && (
                    <div className="text-[10px] text-white/40 italic col-span-3">
                      No backgrounds yet — upload or generate below.
                    </div>
                  )}
                </div>
              </div>

              {/* Upload / AI */}
              <div className="grid grid-cols-2 gap-1.5">
                <label
                  data-testid="vod-bg-upload-label"
                  className="inline-flex items-center justify-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded px-2 py-1.5 text-[10px] font-black uppercase tracking-wider cursor-pointer"
                >
                  <Upload className="w-3 h-3 text-tv-accent" /> Upload BG
                  <input type="file" accept="image/*" className="hidden" onChange={onBgUpload} data-testid="vod-bg-upload" />
                </label>
                <div className="text-[10px] text-white/40 italic self-center">or generate ↓</div>
              </div>
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") onGenerate(); }}
                  placeholder="Sunset rooftop, neon Miami"
                  data-testid="vod-bg-ai-prompt"
                  disabled={generating}
                  className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-[11px] outline-none focus:border-tv-accent disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={onGenerate}
                  disabled={generating || !aiPrompt.trim()}
                  data-testid="vod-bg-ai-gen"
                  className="inline-flex items-center gap-1 bg-tv-accent hover:bg-yellow-400 text-black text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded disabled:opacity-40"
                >
                  {generating ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  Gen
                </button>
              </div>

              <div className="text-[10px] text-white/40 italic pt-1 border-t border-white/10">
                Tip: use &ldquo;Transparent&rdquo; + Export to get a WebM with alpha you can drop into any
                editor. Perfect for reels + IG stitches.
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
